import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Fingerprint } from 'lucide-react';
import { useAuth } from '../../lib/auth.js';
import {
  AppTooOldError,
  authenticatePasskey,
  cancelPasskeyCeremony,
  errorMessage,
  isPasskeyCancelled,
  signInWasInterrupted,
} from '../../lib/passkey.js';
import { Alert, FieldError } from '../../lib/ui.js';
import { playListing } from '../../lib/app-version.js';
import { t } from '../../i18n/index.js';
import { appInfo, hasNativePasskey, isNativeApp, webAuthnUsable } from '../../lib/native.js';

/** An Android phone, in a browser rather than in our app. */
const onAndroidBrowser = (): boolean => !isNativeApp() && /Android/i.test(navigator.userAgent);

export function LoginPage() {
  const { refresh, expired, error: unreachable } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Why fingerprint sign-in cannot work in this app, when it cannot.
   *
   * The app decides at startup whether Android will let its WebView use passkeys, and until now
   * kept the answer to itself. Without it, a WebView that was refused the feature and a person
   * who dismissed the prompt both produce NotAllowedError, and the screen says the same
   * unhelpful thing to each. Employees tapped Sign in eleven times in eleven seconds, and the
   * challenge rows on the server were the only trace.
   */
  const [blocked, setBlocked] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  /**
   * An app old enough to have no way of asking Android for the passkey itself.
   *
   * Such an app can only use the WebView route, which is the route that fails on some phones —
   * and no amount of deploying fixes it, because the fix is in the app. Saying so before anybody
   * taps is the difference between a known problem and a mystery that comes back every week.
   */
  const appTooOld = isNativeApp() && !hasNativePasskey();
  /**
   * The last load happened in the middle of a sign-in.
   *
   * Android reclaims the app while the fingerprint sheet is up, the WebView is rebuilt, and the
   * page comes back exactly as it was — which is why this reads as "it refreshed and stayed on
   * the same page". Read once, on mount, because reading it clears it.
   */
  const [interrupted] = useState(signInWasInterrupted);
  /** What this copy of the app is, for the line at the foot of the screen. Null in a browser. */
  const [info, setInfo] = useState<Awaited<ReturnType<typeof appInfo>>>(null);
  // A prompt still open when the page goes away would block the next one.
  useEffect(() => cancelPasskeyCeremony, []);

  // Asked once, before anybody taps: it is better to say the button cannot work than to let it
  // fail silently. Outside the app there is nothing to ask and nothing to say.
  useEffect(() => {
    if (!isNativeApp()) return;
    void appInfo()
      .then((got) => {
        setInfo(got);
        if (!webAuthnUsable(got)) setBlocked(got?.webauthn_status ?? 'unknown');
      })
      .catch(() => {
        /* the app could not be asked; the sign-in attempt will report for itself */
      });
  }, []);

  const signIn = async () => {
    setBusy(true);
    setError(null);
    setHint(null);
    try {
      await authenticatePasskey('/auth/worker/login/options', '/auth/worker/login/verify');
      await refresh();
    } catch (err) {
      // A cancelled prompt and a WebView that was never granted passkeys are the same error
      // object. When the app has already told us it was refused, say that instead of offering
      // advice about QR codes to somebody whose phone will never show a prompt.
      if (err instanceof AppTooOldError) setError(t('passkey_app_old'));
      else if (isPasskeyCancelled(err) && blocked)
        setError(t('passkey_app_blocked', { why: blocked }));
      else if (isPasskeyCancelled(err)) setHint(t('passkey_cancel_hint'));
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-form">
      <h2 className="auth-title">{t('login_title')}</h2>
      {expired && <Alert kind="info">{t('session_expired')}</Alert>}
      {unreachable && <Alert>{t('api_unreachable')}</Alert>}
      {blocked && <Alert>{t('passkey_app_blocked', { why: blocked })}</Alert>}
      {appTooOld && (
        <Alert kind="warn">
          {t('passkey_app_old')}{' '}
          {/* The Play listing, never our APK page: this only shows inside the app. */}
          <a href={playListing()} rel="noreferrer">
            {t('passkey_app_update')}
          </a>
        </Alert>
      )}
      {interrupted && !error && <Alert kind="info">{t('passkey_interrupted')}</Alert>}
      <div className="passkey-step">
        <div className="passkey-icon" aria-hidden="true">
          <Fingerprint size={34} />
        </div>
        <p className="auth-lead">{t('login_intro')}</p>
        <button type="button" className="big" onClick={signIn} disabled={busy}>
          {busy ? t('passkey_signing_in') : t('passkey_sign_in')}
        </button>
        <FieldError id="passkey-error" message={error} />
        {hint && !error && (
          <p className="muted small-text passkey-hint" role="status">
            {hint}
          </p>
        )}
        <div className="auth-links center">
          <Link to="/reregister">{t('login_new_phone')}</Link>
        </div>
        {/* An Android browser is a worker who has not installed the app, or cannot find it. The
            app is where the location can be trusted, so the route to it belongs here — and only
            here: an iPhone has no app to install, and inside the app this would be nonsense. */}
        {onAndroidBrowser() && (
          <p className="muted small-text center-text get-the-app">
            <Link to="/get-the-app">{t('login_get_the_app')}</Link>
          </p>
        )}
        <p className="muted small-text center-text">{t('login_no_invite')}</p>
        {/* The one number that decides what is wrong when sign-in does nothing, on the screen
            where it does nothing. Somebody who cannot sign in has never punched, so the admin's
            list cannot see them; this is how the office finds out without a phone call. */}
        {info && (
          <p className="muted small-text center-text app-version-line">
            {t('login_app_version', { name: info.version_name, code: info.version_code })}
          </p>
        )}
      </div>
    </div>
  );
}
